import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, getContentSource } from "@/lib/constants";
import axios from "axios";
import { toast } from "sonner";

const QuoteRequestModal = ({ isOpen, onClose, service, serviceName, defaultBudget }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    website: "",
    budget: defaultBudget || "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quotes/request`, {
        ...formData,
        service: service,
        service_name: serviceName
      });

      if (response.data.success) {
        toast.success("Quote request submitted successfully!");
        onClose();
        navigate("/thank-you/lead?from=quote");
      }
    } catch (error) {
      console.error("Error submitting quote request:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      company: "",
      website: "",
      budget: defaultBudget || "",
      message: ""
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-lead-modal data-testid="quote-request-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-xl font-bold text-white">Request a Quote</h2>
            <p className="text-sm text-gray-400 mt-1">{serviceName}</p>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="close-quote-modal"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center py-8" data-testid="quote-success-message">
              <div className="w-16 h-16 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Request Submitted!</h3>
              <p className="text-gray-400 mb-6">
                We'll review your request and send you a custom quote within 24 hours.
              </p>
              <Button onClick={handleClose} className="btn-ninja">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} id="quote-request-form" name="quote-request-form" className="space-y-4" data-testid="quote-request-form">
              <input type="hidden" name="content_source" value={getContentSource()} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">First Name *</label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    placeholder="John"
                    className="bg-white/5 border-white/10"
                    data-testid="quote-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Last Name *</label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    placeholder="Doe"
                    className="bg-white/5 border-white/10"
                    data-testid="quote-last-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                  className="bg-white/5 border-white/10"
                  data-testid="quote-email"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Phone</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="bg-white/5 border-white/10"
                    data-testid="quote-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Company</label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    className="bg-white/5 border-white/10"
                    data-testid="quote-company"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Website</label>
                <Input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://yourwebsite.com"
                  className="bg-white/5 border-white/10"
                  data-testid="quote-website"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Budget Range</label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00E676]/50"
                  data-testid="quote-budget"
                >
                  <option value="">Select budget range</option>
                  <option value="5000-10000">$5,000 - $10,000</option>
                  <option value="10000-25000">$10,000 - $25,000</option>
                  <option value="25000-50000">$25,000 - $50,000</option>
                  <option value="50000+">$50,000+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Tell us about your project *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Describe your goals, timeline, and any specific requirements..."
                  rows={4}
                  className="bg-white/5 border-white/10 resize-none"
                  data-testid="quote-message"
                />
              </div>

              <Button 
                type="submit" 
                className="btn-ninja w-full"
                disabled={isSubmitting}
                data-testid="submit-quote-request"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Request Quote
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteRequestModal;
